import * as React from 'react'

import { styled } from '~/styled'

import type { PaginationPageProps } from './types'
import { usePagination } from './usePagination'

const StyledButton = styled('button', {
  base: [
    'items-center',
    'border',
    'border-transparent',
    'rounded-sm',
    'cursor-pointer',
    'font-body',
    'justify-center',
    'whitespace-nowrap',
    'w-max',
    'flex',
    'flex-col',
    'p-0',
    'font-normal',
    'text-text-subtle',
    'bg-(--base-1)',
    'relative',
    'not-disabled:active:bg-(--base-3)',
    'not-disabled:active:text-(--text-bold)',
    'not-disabled:focus-visible:outline-none',
    'not-disabled:focus-visible:relative',
    'not-disabled:focus-visible:shadow-[var(--color-primary-800)_0px_0px_0px_4px]',
    'not-disabled:focus-visible:shadow-[white_0px_0px_0px_2px]',
    'not-disabled:focus-visible:z-1',
    'not-disabled:hover:bg-(--base-2)',
    'not-disabled:hover:text-text-regular',
    'disabled:cursor-not-allowed',
    'disabled:opacity-30',
    'disabled:pointer-events-none'
  ],
  variants: {
    size: {
      md: ['size-10']
    },
    selected: {
      true: [
        'border',
        'border-(--accent-9)',
        'font-semibold',
        'not-disabled:active:border-(--accent-11)',
        'not-disabled:active:text-(--accent-11)',
        'not-disabled:hover:border-(--accent-10)',
        'not-disabled:hover:text-(--accent-10)',
        'text-(--accent-9)'
      ]
    },
    indicated: {
      true: [
        'bg-(--accent-9)',
        'font-semibold',
        'not-disabled:active:bg-(--accent-11)',
        'not-disabled:active:text-white',
        'not-disabled:hover:bg-(--accent-10)',
        'not-disabled:hover:text-white',
        'text-white'
      ]
    }
  },
  compoundVariants: [
    {
      selected: true,
      indicated: true,
      class: [
        'bg-(--accent-9)',
        'font-semibold',
        'not-disabled:active:bg-(--accent-11)',
        'not-disabled:active:text-white',
        'not-disabled:hover:bg-(--accent-10)',
        'not-disabled:hover:text-white',
        'not-focus-visible:border-white!',
        'shadow-[var(--accent-9)_0px_0px_0px_1px]',
        'text-white'
      ]
    }
  ]
})

export const PaginationPage = ({
  pageNumber,
  className,
  onClick
}: PaginationPageProps) => {
  const { currentPage, goToPage, indicatedPages, disabledPages, onItemHover } =
    usePagination()

  const isIndicated = indicatedPages.includes(pageNumber)
  const isDisabled = disabledPages.includes(pageNumber)

  const isSelected = currentPage === pageNumber

  const handleOnHover = () => {
    if (isSelected || isDisabled) return
    onItemHover?.(pageNumber)
  }

  const handleOnClick = (pageNumber) => {
    onClick?.()
    goToPage(pageNumber)
  }

  return (
    <StyledButton
      selected={isSelected}
      size="md"
      onClick={() => handleOnClick(pageNumber)}
      className={className}
      indicated={isIndicated}
      disabled={isDisabled}
      aria-current={isSelected && 'page'}
      aria-disabled={isDisabled}
      onMouseOver={handleOnHover}
    >
      {pageNumber}
    </StyledButton>
  )
}
